import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { capitalize, formatDate } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import { IconDots, IconEye, IconPencil, IconPlus, IconX } from "@tabler/icons-react"

interface TimeOffRequest {
  id: number
  request_type: string
  status: string
  start_date: string
  end_date: string
  days_requested: number
  can_edit: boolean
  can_cancel: boolean
}

interface StatusCounts {
  all: number
  pending: number
  approved: number
  denied: number
  cancelled: number
}

interface Props {
  [key: string]: unknown
  requests: TimeOffRequest[]
  pagy: PagyMeta
  status_counts: StatusCounts
  filters: { status?: string; type?: string }
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  approved: "default",
  pending: "secondary",
  denied: "destructive",
  cancelled: "outline",
}

export default function TimeOffRequestsIndex() {
  const { requests, pagy, status_counts, filters } = usePage<Props>().props

  function handleFilter(status?: string) {
    router.get(
      "/time_off_requests",
      {
        status,
        type: filters.type || undefined,
      },
      { preserveState: true },
    )
  }

  function handleCancel(id: number) {
    if (confirm("Are you sure you want to cancel this request?")) {
      router.patch(`/time_off_requests/${id}/cancel`)
    }
  }

  const activeStatus = filters.status

  return (
    <>
      <Head title="My Requests - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>My Requests</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">My Requests</h1>
          <Button size="sm" asChild>
            <a href="/time_off_requests/new">
              <IconPlus className="mr-1 h-4 w-4" />
              New Request
            </a>
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground hidden text-sm sm:inline">Show:</span>
          {(
            [
              { label: "All", value: undefined, count: status_counts.all },
              { label: "Approved", value: "approved", count: status_counts.approved },
              { label: "Pending", value: "pending", count: status_counts.pending },
              { label: "Denied", value: "denied", count: status_counts.denied },
              { label: "Cancelled", value: "cancelled", count: status_counts.cancelled },
            ] as const
          ).map((filter) => (
            <Button
              key={filter.label}
              variant={activeStatus === filter.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilter(filter.value)}
            >
              {filter.label} ({filter.count})
            </Button>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Days</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell className="font-medium">
                  <a href={`/time_off_requests/${request.id}`} className="hover:underline">
                    #{request.id}
                  </a>
                </TableCell>
                <TableCell className="capitalize">{request.request_type}</TableCell>
                <TableCell>{formatDate(request.start_date)}</TableCell>
                <TableCell>{formatDate(request.end_date)}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[request.status] || "outline"}>{capitalize(request.status)}</Badge>
                </TableCell>
                <TableCell className="text-right">{request.days_requested}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <IconDots className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <a href={`/time_off_requests/${request.id}`}>
                          <IconEye className="mr-2 h-4 w-4" />
                          View
                        </a>
                      </DropdownMenuItem>
                      {request.can_edit && (
                        <DropdownMenuItem asChild>
                          <a href={`/time_off_requests/${request.id}/edit`}>
                            <IconPencil className="mr-2 h-4 w-4" />
                            Edit
                          </a>
                        </DropdownMenuItem>
                      )}
                      {request.can_cancel && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onClick={() => handleCancel(request.id)}>
                            <IconX className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground text-center">
                  No requests found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

TimeOffRequestsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
